import React, { useEffect, useRef, useState } from 'react';
import { X, Plus, RefreshCw } from 'lucide-react';
import { STRATEGIES, suggestPrefix } from '../config/codeStrategies';
import { apiFetch } from '../../../context/AuthContext';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AddCompanyModal = ({ theme, styles, onAdd, onClose }) => {
  const [newCompanyName, setNewCompanyName] = useState('');
  const [maxEmployees, setMaxEmployees] = useState('50');
  const [maxUsers, setMaxUsers] = useState('5');
  const [adding, setAdding] = useState(false);

  const [prefix, setPrefix] = useState('');
  const [prefixTouched, setPrefixTouched] = useState(false);
  const [strategy, setStrategy] = useState('SEQUENTIAL');
  const [bodyLength, setBodyLength] = useState(4);
  const [prefixError, setPrefixError] = useState('');
  const [previewCodes, setPreviewCodes] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const debounceRef = useRef(null);

  const labelStyle = {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    color: theme === "light" ? "#374151" : "rgba(255, 255, 255, 0.9)",
    marginBottom: "8px",
  };

  // Auto-suggest a prefix from the company name until the admin edits the
  // prefix field by hand — after that, typing the name no longer overrides
  // their choice.
  useEffect(() => {
    if (!prefixTouched) {
      setPrefix(suggestPrefix(newCompanyName));
    }
  }, [newCompanyName, prefixTouched]);

  // Debounced live preview against the real backend renderer — no DB
  // writes, works even though this client doesn't exist yet.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!prefix.trim()) {
      setPreviewCodes([]);
      return undefined;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        setPreviewLoading(true);
        const response = await apiFetch(`${BASE_URL}/api/configurations/code-policy/preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prefix,
            separator: '-',
            strategy,
            bodyLength,
            padWithZeros: true,
            startAt: 1,
            casing: 'UPPER',
            count: 3,
          }),
        });
        const data = await response.json();
        setPreviewCodes(response.ok ? (data?.data?.codes || []) : []);
      } catch {
        setPreviewCodes([]);
      } finally {
        setPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [prefix, strategy, bodyLength]);

  const handleAdd = async () => {
    const trimmedPrefix = prefix.trim().toUpperCase();
    if (!trimmedPrefix) {
      setPrefixError('An Infinity Code prefix is required for every client.');
      return;
    }
    if (!/^[A-Za-z0-9]{1,12}$/.test(trimmedPrefix)) {
      setPrefixError('Prefix must be 1-12 letters/numbers, no spaces or punctuation.');
      return;
    }
    setPrefixError('');
    setAdding(true);
    const success = await onAdd(newCompanyName, {
      maxEmployees: maxEmployees === '' ? null : Number(maxEmployees),
      maxUsers: maxUsers === '' ? null : Number(maxUsers),
      codePolicy: {
        prefix: trimmedPrefix,
        separator: '-',
        strategy,
        bodyLength,
        padWithZeros: true,
        startAt: 1,
        casing: 'UPPER',
      },
    });
    setAdding(false);
    if (success) {
      setNewCompanyName('');
      setMaxEmployees('50');
      setMaxUsers('5');
      setPrefix('');
      setPrefixTouched(false);
      setStrategy('SEQUENTIAL');
      setBodyLength(4);
      onClose();
    }
  };

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Add New Company</h3>
          <button style={styles.closeButton} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div>
          <label style={labelStyle}>
            Company Name
          </label>
          <input
            type="text"
            value={newCompanyName}
            onChange={(e) => setNewCompanyName(e.target.value)}
            placeholder="Enter company name..."
            style={styles.input}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !adding) handleAdd();
            }}
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <div>
            <label style={labelStyle}>Infinity Code Prefix</label>
            <input
              type="text"
              value={prefix}
              onChange={(e) => {
                setPrefixTouched(true);
                setPrefix(e.target.value.toUpperCase());
                setPrefixError('');
              }}
              placeholder="RAY"
              maxLength={12}
              style={{ ...styles.input, marginBottom: prefixError ? '6px' : '24px' }}
            />
            {prefixError && (
              <p style={{ fontSize: '11.5px', color: '#dc2626', margin: '0 0 24px' }}>{prefixError}</p>
            )}
          </div>
          <div>
            <label style={labelStyle}>Code Format</label>
            <select
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
              style={styles.input}
            >
              {STRATEGIES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{
          background: theme === 'light' ? 'rgba(139,92,246,0.05)' : 'rgba(139,92,246,0.1)',
          border: theme === 'light' ? '1px dashed rgba(139,92,246,0.3)' : '1px dashed rgba(139,92,246,0.35)',
          borderRadius: '10px',
          padding: '12px 14px',
          marginTop: '-8px',
          marginBottom: '20px',
        }}>
          <div style={{ fontSize: '10.5px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px', color: '#8b5cf6', marginBottom: '6px' }}>
            Next codes will look like
          </div>
          {previewLoading ? (
            <span style={{ fontSize: '12px', opacity: 0.6 }}>Generating preview…</span>
          ) : previewCodes.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {previewCodes.map((code, i) => (
                <code key={i} style={{
                  fontSize: '12px', fontWeight: '600', padding: '3px 9px', borderRadius: '6px',
                  background: theme === 'light' ? '#ffffff' : 'rgba(255,255,255,0.08)',
                  color: theme === 'light' ? '#0f172a' : '#ffffff',
                }}>
                  {code}
                </code>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: '12px', opacity: 0.5 }}>Enter a prefix to preview</span>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <div>
            <label style={labelStyle}>Employee Data Quota</label>
            <input
              type="number"
              min="0"
              value={maxEmployees}
              onChange={(e) => setMaxEmployees(e.target.value)}
              placeholder="Leave blank for unlimited"
              style={styles.input}
            />
            <p style={{ fontSize: "11.5px", opacity: 0.65, margin: "6px 0 0" }}>
              Max employees with processable data (attendance, payroll, etc).
            </p>
          </div>
          <div>
            <label style={labelStyle}>Employee Account Quota</label>
            <input
              type="number"
              min="0"
              value={maxUsers}
              onChange={(e) => setMaxUsers(e.target.value)}
              placeholder="Leave blank for unlimited"
              style={styles.input}
            />
            <p style={{ fontSize: "11.5px", opacity: 0.65, margin: "6px 0 0" }}>
              Max employees who also get a login account (email/phone + password).
              Counts toward the employee quota above, not on top of it.
            </p>
          </div>
        </div>

        <div style={styles.modalButtons}>
          <button style={styles.cancelButton} onClick={onClose} disabled={adding}>
            Cancel
          </button>
          <button style={styles.submitButton} onClick={handleAdd} disabled={adding}>
            {adding ? (
              <>
                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Creating...
              </>
            ) : (
              <>
                <Plus size={16} />
                Create Company
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCompanyModal;
